/**
 * API client for loops — SP2.0 proxy via /api/v1/loops
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Search/filter loops. All 13 query params forwarded to SP2.
 * @param {Object} filters — { q, mood, vibe, style, key, bpm, bpm_min, bpm_max, instrument, energy, pack, limit, offset }
 */
export async function fetchLoops(filters = {}) {
  const params = new URLSearchParams();
  if (filters.q) params.set('q', filters.q);
  if (filters.mood) params.set('mood', filters.mood);
  if (filters.vibe) params.set('vibe', filters.vibe);
  if (filters.style) params.set('style', filters.style);
  if (filters.key) params.set('key', filters.key);
  if (filters.bpm) params.set('bpm', filters.bpm);
  if (filters.bpm_min) params.set('bpm_min', filters.bpm_min);
  if (filters.bpm_max) params.set('bpm_max', filters.bpm_max);
  if (filters.instrument) params.set('instrument', filters.instrument);
  if (filters.energy) params.set('energy', filters.energy);
  if (filters.pack) params.set('pack', filters.pack);
  if (filters.limit) params.set('limit', filters.limit);
  if (filters.offset) params.set('offset', filters.offset);

  const res = await fetch(`${BACKEND_URL}/api/v1/loops?${params}`);
  if (!res.ok) throw new Error(`Failed to fetch loops: ${res.status}`);
  return res.json();
}

/** Get single loop metadata */
export async function fetchLoop(loopId) {
  const res = await fetch(`${BACKEND_URL}/api/v1/loops/${loopId}`);
  if (!res.ok) throw new Error(`Failed to fetch loop ${loopId}: ${res.status}`);
  return res.json();
}

/** Get loop audio URL (for streaming/preview) */
export function getLoopAudioUrl(loopId) {
  if (String(loopId).startsWith('pattern-')) return null;
  return `${BACKEND_URL}/api/v1/loops/${loopId}/audio`;
}

/**
 * Audio URL cache — maps loop_id to direct audio_url (from SP2 data).
 * When loops are fetched from the API, their audio_urls are stored here.
 * This avoids the proxy /audio endpoint when direct R2 URLs are available.
 */
const audioUrlCache = new Map();

export function cacheAudioUrl(loopId, audioUrl) {
  if (audioUrl) audioUrlCache.set(loopId, audioUrl);
}

export function getCachedAudioUrl(loopId) {
  return audioUrlCache.get(loopId);
}

/**
 * Wrap a direct R2 presigned URL through the audio proxy to avoid CORS issues.
 * Only proxies URLs from r2.cloudflarestorage.com.
 * Non-R2 URLs (e.g., our own /api/v1/loops/:id/audio) pass through unchanged.
 */
function proxyIfNeeded(url) {
  if (!url) return url;
  if (url.includes('r2.cloudflarestorage.com')) {
    return `${BACKEND_URL}/api/v1/audio-proxy?url=${encodeURIComponent(url)}`;
  }
  return url;
}

/**
 * Resolve the best audio URL for a loop:
 * 1. Direct R2 URL from SP2 (audio_url field) — routed through proxy for CORS
 * 2. Backend loop audio endpoint fallback
 */
export function resolveAudioUrl(loopId, clip = null) {
  if (clip?.isPattern || String(loopId).startsWith('pattern-')) return null;
  const cached = audioUrlCache.get(loopId);
  if (cached) return proxyIfNeeded(cached);
  return getLoopAudioUrl(loopId);
}

/** List all themed packs */
export async function fetchPacks() {
  const res = await fetch(`${BACKEND_URL}/api/v1/packs`);
  if (!res.ok) throw new Error(`Failed to fetch packs: ${res.status}`);
  return res.json();
}

/** Get loops in a themed pack */
export async function fetchPackLoops(packId) {
  const res = await fetch(`${BACKEND_URL}/api/v1/packs/${packId}/loops`);
  if (!res.ok) throw new Error(`Failed to fetch pack loops: ${res.status}`);
  return res.json();
}

/** Get user's favorites (requires auth) */
export async function fetchFavorites(token) {
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const res = await fetch(`${BACKEND_URL}/api/v1/favorites`, { headers });
  if (!res.ok) throw new Error(`Failed to fetch favorites: ${res.status}`);
  return res.json();
}
